/**
 * Add-to-cart tracking for Klaviyo and GA4/GTM (replaces the old theme's
 * fetch monkey-patch, which only covered Klaviyo on product pages).
 *
 * The theme dispatches a bubbling `variant:add` event for every add-to-cart
 * path (PDP, sticky bar, quick buy, product card size buttons, cross-sells)
 * with the added items and the full /cart.js payload in the detail — so we
 * listen for that instead of intercepting network calls.
 *
 * Also re-dispatches the old theme's `cart:updated` CustomEvent so any GTM
 * triggers built against it keep working.
 */
(() => {
  function toMoney(cents) {
    return typeof cents === 'number' ? cents / 100 : undefined;
  }

  function absoluteUrl(path) {
    return path ? window.location.origin + path : undefined;
  }

  document.addEventListener('variant:add', (event) => {
    const detail = event.detail || {};
    const cart = detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    const addedItems = Array.isArray(detail.items) ? detail.items : [];
    const added = addedItems[0] || {};

    // --- Klaviyo "Added to Cart" ---
    // Superset of the old theme's payload (kept for compatibility with existing
    // flows/segments/templates) and Klaviyo's recommended AddedItem* spec.
    // Fixes from the old theme: total_discount is now in dollars like the other
    // money fields, and the added product's fields come from the actual added
    // line instead of Klaviyo's viewed-product blob. `items` stays the raw
    // /cart.js array (prices in cents) exactly as the old theme sent it; the
    // normalized dollar values live in `Items`.
    const klaviyoPayload = {
      // Old theme keys
      total_price: toMoney(cart.total_price),
      $value: toMoney(cart.total_price),
      total_discount: toMoney(cart.total_discount),
      original_total_price: toMoney(cart.original_total_price),
      items: cart.items,
      // Old theme merged Klaviyo's viewed-product object; same keys, sourced
      // from the added line item (Categories is not available client-side)
      Name: added.product_title,
      ProductID: added.product_id,
      ImageURL: added.image,
      URL: absoluteUrl(added.url),
      Brand: added.vendor,
      Price: toMoney(added.price),
      // Klaviyo recommended spec
      AddedItemProductName: added.product_title,
      AddedItemProductID: added.product_id,
      AddedItemSKU: added.sku,
      AddedItemVariantID: added.variant_id,
      AddedItemVariantName: added.variant_title,
      AddedItemPrice: toMoney(added.price),
      AddedItemQuantity: added.quantity,
      AddedItemURL: absoluteUrl(added.url),
      AddedItemImageURL: added.image,
      ItemNames: cart.items.map((item) => item.product_title),
      CheckoutURL: window.location.origin + '/cart',
      Items: cart.items.map((item) => ({
        ProductID: item.product_id,
        SKU: item.sku,
        ProductName: item.product_title,
        VariantID: item.variant_id,
        VariantName: item.variant_title,
        Quantity: item.quantity,
        ItemPrice: toMoney(item.price),
        RowTotal: toMoney(item.final_line_price != null ? item.final_line_price : item.line_price),
        ProductURL: absoluteUrl(item.url),
        ImageURL: item.image,
      })),
    };

    // Send straight to the NOOKIE account. The onsite queue can be claimed by
    // the leftover UPsRnk script, and that account was only retaining events
    // for an already identified (logged-in) profile. Guests get a stable
    // anonymous id so the metric still records the add.
    const companyId = 'WKuNfJ';
    const profile = {};
    if (window.__nookieCustomerEmail) profile.email = window.__nookieCustomerEmail;

    const klaCookie = document.cookie.match(/(?:^|; )__kla_id=([^;]*)/);
    if (klaCookie) {
      try {
        const kla = JSON.parse(atob(decodeURIComponent(klaCookie[1])));
        if ((!kla.cid || kla.cid === companyId) && kla.$exchange_id) profile._kx = kla.$exchange_id;
      } catch (error) {
        /* cookie is not the NOOKIE exchange id */
      }
    }

    if (!profile.email && !profile._kx) {
      const storageKey = 'nookie_klaviyo_anon';
      let anonId = '';
      try {
        anonId = localStorage.getItem(storageKey) || '';
        if (!anonId) {
          anonId = (window.crypto && crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
          localStorage.setItem(storageKey, anonId);
        }
      } catch (error) {
        anonId = String(Date.now());
      }
      profile.anonymous_id = anonId;
    }

    fetch('https://a.klaviyo.com/client/events?company_id=' + companyId, {
      method: 'POST',
      headers: {
        'content-type': 'application/vnd.api+json',
        revision: '2024-10-15',
      },
      keepalive: true,
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: klaviyoPayload,
            value: klaviyoPayload.$value,
            unique_id: String(added.variant_id || added.product_id || 'cart') + '-' + Date.now(),
            metric: {
              data: { type: 'metric', attributes: { name: 'Added to Cart' } },
            },
            profile: { data: { type: 'profile', attributes: profile } },
          },
        },
      }),
    }).catch(function () {});

    // --- GA4 add_to_cart via GTM dataLayer ---
    const addedValue = addedItems.reduce((total, item) => {
      const line = item.final_line_price != null ? item.final_line_price : (item.price || 0) * (item.quantity || 1);
      return total + line;
    }, 0);

    window.dataLayer = window.dataLayer || [];
    // Clear the previous ecommerce object so events don't inherit stale items
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: cart.currency,
        value: toMoney(addedValue),
        items: addedItems.map((item) => ({
          item_id: item.sku || String(item.variant_id || ''),
          item_name: item.product_title,
          item_variant: item.variant_title,
          item_brand: item.vendor,
          price: toMoney(item.price),
          quantity: item.quantity,
        })),
      },
    });

    // Old theme compatibility: GTM may have triggers listening for this.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  });
})();
